import { colors } from 'constants-colors';

/**
 * Status label mapping for work orders
 * Maps API status values to human-readable labels
 */
export const STATUS_LABEL_MAP: Record<string, string> = {
  open: 'Open',
  created: 'Created',
  approval_pending: 'Pending Approval',
  approval_rejected: 'Approval Rejected',
  assignment_pending: 'Pending Acknowledgement',
  assignment_proposal: 'SLA Proposal Pending',
  assignment_rejected: 'Assignment Rejected',
  operative_assignment_rejected: 'Job Operative Assignment Rejected',
  acknowledged: 'Acknowledged',
  commenced: 'In Progress',
  missed: 'Missed',
  cancelled: 'Cancelled',
  completed: 'Completed',
  awaiting_paperwork: 'Awaiting Paperwork',
  paperwork_rejected: 'Paperwork Rejected',
  extension_pending: 'Extension Pending',
};

/**
 * Status color mapping for work orders
 * Maps API status values to color values for UI display
 */
export const STATUS_COLOR_MAP: Record<string, string> = {
  open: colors.laneGreen,
  created: colors.interactiveBlue,
  approval_pending: colors.laneGreen,
  approval_rejected: colors.honey,
  assignment_pending: colors.functionalCritical,
  acknowledged: colors.laneGreen,
  commenced: colors.neutral.grey900,
  missed: colors.functionalCritical,
  cancelled: colors.functionalCritical,
  completed: colors.laneGreen,
  awaiting_paperwork: colors.honey,
  paperwork_rejected: colors.functionalCritical,
  extension_pending: colors.honey,
  assignment_proposal: colors.neutral.grey900,
  assignment_rejected: colors.neutral.grey900,
  operative_assignment_rejected: colors.neutral.grey900,
};

/**
 * Get human-readable status label
 */
export function getStatusLabel(status: string): string {
  return STATUS_LABEL_MAP[status] ?? status ?? '';
}

/**
 * Get status indicator color
 */
export function getStatusColor(status: string): string {
  return STATUS_COLOR_MAP[status] ?? colors.neutral.grey900;
}

/**
 * Check if a work order status is considered closed/finalized
 * Closed statuses include:
 * - 'completed'
 * - 'cancelled'
 * - 'missed'
 * - 'approval_rejected'
 * 
 * Work orders with closed statuses should:
 * - Appear in the "Closed" tab in the work order list
 * - Not be cancellable in the detail view
 * 
 * @param status - The work order status to check (case-insensitive)
 * @returns true if the status is a closed status, false otherwise
 */
export function isWorkOrderClosed(status: string | undefined | null): boolean {
  if (!status) {
    return false;
  }
  
  const normalizedStatus = status.toLowerCase();
  
  // A work order is closed if it matches one of these final states
  return normalizedStatus === 'completed' || 
         normalizedStatus === 'cancelled' || 
         normalizedStatus === 'missed' ||
         normalizedStatus === 'approval_rejected';
}
